import React, { useState, useEffect, useReducer, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { debounce } from "lodash";
import { Virtuoso } from "react-virtuoso";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tooltip } from "react-tooltip";

// Reducer for state management
const initialState = {
  inquiries: [],
  loading: false,
  error: null,
  isSending: false,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "SET_INQUIRIES":
      return { ...state, inquiries: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_SENDING":
      return { ...state, isSending: action.payload };
    default:
      return state;
  }
};

// Input components from SendInquiry.jsx
const TextInput = ({ label, name, value, onChange, placeholder, type = "text", error, icon, className }) => (
  <div className={className}>
    <label className="block text-gray-700 font-medium mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg text-gray-500">
        {typeof icon === "string" ? icon : icon}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className={`w-full pl-10 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
          error ? "border-red-500" : "border-gray-300"
        } focus:outline-none`}
      />
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const TextAreaInput = ({ label, name, value, onChange, placeholder, error, icon }) => (
  <div>
    <label className="block text-gray-700 font-medium mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <span className="absolute left-3 top-4 text-lg text-gray-500">
        {icon}
      </span>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className={`w-full pl-10 p-3 border rounded-lg h-32 focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
          error ? "border-red-500" : "border-gray-300"
        } focus:outline-none resize-none`}
      />
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const SelectInput = ({ label, name, value, onChange, options, error, icon, className }) => (
  <div className={className}>
    <label className="block text-gray-700 font-medium mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">
        {icon}
      </span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required
        className={`w-full pl-10 p-3 border rounded-lg text-gray-600 focus:ring-2 focus:ring-blue-500 transition-all duration-200 appearance-none ${
          error ? "border-red-500" : "border-gray-300"
        } focus:outline-none`}
      >
        <option value="" disabled>
          {name === "countryCode" ? "Select Country..." : "Choose a Course..."}
        </option>
        {options.map((opt) => (
          <option key={opt} value={name === "countryCode" ? opt.split(" ")[0] : opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const Inquiry = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { inquiries, loading, error, isSending } = state;
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [messagedContacts, setMessagedContacts] = useState(() => {
    const saved = localStorage.getItem("messagedContacts");
    return saved ? JSON.parse(saved) : [];
  });
  const [activeTab, setActiveTab] = useState("view");
  const [showConfirm, setShowConfirm] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortConfig, setSortConfig] = useState([{ key: "createdAt", direction: "desc" }]);
  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const inquiriesPerPage = 10;
  const listHeight = 500;

  const [formData, setFormData] = useState({
    course: "",
    fullName: "",
    email: "",
    countryCode: "+977", // Default to Nepal
    mobileNumber: "",
    message: "",
    status: "Pending",
  });

  const courses = [
    "Full Stack MERN", "React Native", "Python", "Data Science", "Django",
    "DevOps", "Flutter App Development", "UI/UX Design", "Frontend Web Development",
    "Full Stack Java", "Digital Marketing", "AI", "RHCSA", "RHCE", "Oracle",
    "AI & ML", "CCNA", "CCNP",
  ];

  const countryCodes = [
    { code: "+1", name: "United States" },
    { code: "+44", name: "United Kingdom" },
    { code: "+91", name: "India" },
    { code: "+977", name: "Nepal" },
    { code: "+61", name: "Australia" },
    { code: "+81", name: "Japan" },
    { code: "+86", name: "China" },
    { code: "+49", name: "Germany" },
    { code: "+33", name: "France" },
  ];

  // Persist messagedContacts in localStorage
  useEffect(() => {
    localStorage.setItem("messagedContacts", JSON.stringify(messagedContacts));
  }, [messagedContacts]);

  // Fetch inquiries with retry logic
  const fetchInquiries = useCallback(async (retryCount = 3) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });
      const response = await fetch("/api/inquiries");
      if (!response.ok) throw new Error("Failed to fetch inquiries");
      const data = await response.json();
      const normalizedData = data.map((inq) => ({
        ...inq,
        id: inq.id.toString(),
        createdAt: new Date(inq.createdAt),
        status: inq.status || "Pending",
      }));
      dispatch({ type: "SET_INQUIRIES", payload: normalizedData });
    } catch (err) {
      if (retryCount > 0) {
        setTimeout(() => fetchInquiries(retryCount - 1), 2000);
      } else {
        dispatch({ type: "SET_ERROR", payload: err.message || "Error loading inquiries" });
        toast.error("Failed to load inquiries after retries");
      }
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const validateCreateForm = () => {
    const newErrors = {};
    if (!formData.course) newErrors.course = "Please select a course";
    if (!formData.fullName) newErrors.fullName = "Please enter your full name";
    if (!formData.email) newErrors.email = "Please enter your email";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.mobileNumber) newErrors.mobileNumber = "Please enter your mobile number";
    else if (!/^\d{7,15}$/.test(formData.mobileNumber)) newErrors.mobileNumber = "Mobile number must be 7-15 digits";
    if (!formData.message) newErrors.message = "Please enter your message";
    return newErrors;
  };

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setCreateErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const formErrors = validateCreateForm();
    if (Object.keys(formErrors).length > 0) {
      setCreateErrors(formErrors);
      return;
    }
    setShowCreateConfirmation(true);
  };

  const confirmCreateSubmission = async () => {
    setShowCreateConfirmation(false);
    dispatch({ type: "SET_LOADING", payload: true });

    try {
      const response = await fetch("/api/inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          mobileNumber: `${formData.countryCode}${formData.mobileNumber}`,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create inquiry");
      }
      setFormData({
        course: "",
        fullName: "",
        email: "",
        countryCode: "+977",
        mobileNumber: "",
        message: "",
        status: "Pending",
      });
      await fetchInquiries();
      setActiveTab("view");
      setCurrentPage(1);
      toast.success("Inquiry created successfully!");
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message || "Error creating inquiry" });
      toast.error(err.message || "Error creating inquiry");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const response = await fetch(`/api/inquiries/${showEditModal}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          mobileNumber: `${formData.countryCode}${formData.mobileNumber}`,
        }),
      });
      if (!response.ok) throw new Error("Failed to update inquiry");
      setShowEditModal(null);
      setFormData({
        course: "",
        fullName: "",
        email: "",
        countryCode: "+977",
        mobileNumber: "",
        message: "",
        status: "Pending",
      });
      await fetchInquiries();
      toast.success("Inquiry updated successfully!");
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message || "Error updating inquiry" });
      toast.error(err.message || "Error updating inquiry");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleDelete = async (id) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const response = await fetch(`/api/inquiries/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete inquiry");
      await fetchInquiries();
      toast.success("Inquiry deleted successfully!");
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message || "Error deleting inquiry" });
      toast.error(err.message || "Error deleting inquiry");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleBatchDelete = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      await Promise.all(
        selectedContacts.map((id) =>
          fetch(`/api/inquiries/${id}`, { method: "DELETE" })
        )
      );
      setSelectedContacts([]);
      await fetchInquiries();
      toast.success("Selected inquiries deleted successfully!");
    } catch (err) {
      dispatch({ type: "SET_ERROR", payload: err.message || "Error deleting inquiries" });
      toast.error(err.message || "Error deleting inquiries");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
      setShowConfirm(null);
    }
  };

  const handleMarkAsResolved = async (id) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const response = await fetch(`/api/inquiries/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "Resolved" }),
      });
      if (!response.ok) throw new Error("Failed to mark as resolved");
      await fetchInquiries();
      toast.success("Inquiry marked as resolved!");
    } catch (err) {
      toast.error(err.message || "Error marking inquiry as resolved");
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleBatchMarkAsResolved = async () => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      await Promise.all(
        selectedContacts.map((id) =>
          fetch(`/api/inquiries/${id}/status`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "Resolved" }),
          })
        )
      );
      setSelectedContacts([]);
      await fetchInquiries();
      toast.success("Selected inquiries marked as resolved!");
    } catch (err) {
      toast.error(err.message || "Error marking inquiries as resolved");
    } finally {
      dispatch({ type: "SET_SENDING", payload: false });
      dispatch({ type: "SET_LOADING", payload: false });
      setShowConfirm(null);
    }
  };

  const handleSendMessages = async (ids) => {
    dispatch({ type: "SET_SENDING", payload: true });
    try {
      for (const id of ids) {
        const inquiry = inquiries.find((inq) => inq.id === id);
        const phoneNumber = inquiry.mobileNumber.replace(/[^0-9]/g, "");
        const message = encodeURIComponent(
          `Hello ${inquiry.fullName}, thank you for your inquiry about ${inquiry.course}. We will get back to you soon!`
        );
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
        const newWindow = window.open(whatsappUrl, "_blank");

        if (newWindow) {
          const userConfirmed = window.confirm(
            `Did you successfully send the WhatsApp message to ${inquiry.fullName}?`
          );
          if (userConfirmed) {
            setMessagedContacts((prev) => [...prev, id]);
            await fetch(`/api/inquiries/${id}/status`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ status: "Messaged" }),
            });
          }
        } else {
          throw new Error("Failed to open WhatsApp");
        }
      }
      await fetchInquiries();
      toast.success("Messages sent successfully!");
    } catch (err) {
      toast.error("Error sending messages: " + err.message);
    } finally {
      dispatch({ type: "SET_SENDING", payload: false });
      setSelectedContacts([]);
      setShowConfirm(null);
    }
  };

  const handleSendEmail = async (inquiry) => {
    try {
      const emailBody = encodeURIComponent(
        `Subject: Response to Your Inquiry About ${inquiry.course}\n\n` +
        `Dear ${inquiry.fullName},\n\n` +
        `Thank you for your inquiry about the ${inquiry.course} course at Virtueista IT. We will get back to you soon with more details.\n\n` +
        `Best regards,\nVirtueista IT Team`
      );
      window.location.href = `mailto:${inquiry.email}?body=${emailBody}`;
      const userConfirmed = window.confirm(
        `Did you successfully send the email to ${inquiry.fullName}?`
      );
      if (userConfirmed) {
        setMessagedContacts((prev) => [...prev, inquiry.id]);
        await fetch(`/api/inquiries/${inquiry.id}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "Messaged" }),
        });
        await fetchInquiries();
        toast.success("Email sent successfully!");
      }
    } catch (err) {
      toast.error("Error sending email: " + err.message);
    }
  };

  const exportToCSV = () => {
    const headers = ["InquiryID,Course,Full Name,Email,Mobile,Message,Status,Created At"];
    const rows = filteredInquiries.map((inq) => [
      inq.id,
      inq.course,
      inq.fullName,
      inq.email,
      inq.mobileNumber,
      `"${inq.message.replace(/"/g, '""')}"`,
      inq.status,
      format(inq.createdAt, "yyyy-MM-dd HH:mm:ss"),
    ].join(","));
    const csv = [...headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inquiries.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Inquiries exported to CSV!");
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const currentSort = prev.find((sort) => sort.key === key);
      if (currentSort) {
        return [{ key, direction: currentSort.direction === "asc" ? "desc" : "asc" }];
      }
      return [{ key, direction: "asc" }];
    });
  };

  const debouncedSetSearchTerm = useCallback(
    debounce((value) => setSearchTerm(value), 300),
    []
  );

  const filteredInquiries = useMemo(() => {
    let result = [...inquiries];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (inq) =>
          inq.fullName.toLowerCase().includes(term) ||
          inq.email.toLowerCase().includes(term) ||
          inq.course.toLowerCase().includes(term)
      );
    }

    if (courseFilter) {
      result = result.filter((inq) => inq.course === courseFilter);
    }

    if (statusFilter) {
      if (statusFilter === "Messaged") {
        result = result.filter((inq) => inq.status === "Messaged" || messagedContacts.includes(inq.id));
      } else if (statusFilter === "Pending") {
        result = result.filter((inq) => inq.status === "Pending" && !messagedContacts.includes(inq.id));
      } else if (statusFilter === "Resolved") {
        result = result.filter((inq) => inq.status === "Resolved");
      }
    }

    if (dateRange.start && dateRange.end) {
      const start = new Date(dateRange.start);
      const end = new Date(dateRange.end);
      result = result.filter(
        (inq) => inq.createdAt >= start && inq.createdAt <= end
      );
    }

    if (sortConfig.length > 0) {
      const { key, direction } = sortConfig[0];
      result.sort((a, b) => {
        let aValue = a[key];
        let bValue = b[key];

        if (key === "id") {
          aValue = parseInt(aValue, 10);
          bValue = parseInt(bValue, 10);
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        if (key === "createdAt") {
          return direction === "asc" ? aValue - bValue : bValue - aValue;
        }

        return direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }

    return result;
  }, [inquiries, searchTerm, courseFilter, statusFilter, dateRange, sortConfig, messagedContacts]);

  const totalPages = Math.ceil(filteredInquiries.length / inquiriesPerPage);
  const currentInquiries = filteredInquiries.slice(
    (currentPage - 1) * inquiriesPerPage,
    currentPage * inquiriesPerPage
  );

  const handleSelectContact = (id) => {
    setSelectedContacts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === currentInquiries.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(currentInquiries.map((inq) => inq.id));
    }
  };

  const renderViewRow = ({ index, data }) => {
    const inquiry = data[index];
    return (
      <div
        className={`flex border-b ${
          messagedContacts.includes(inquiry.id)
            ? "bg-green-100"
            : inquiry.status === "Resolved"
            ? "bg-gray-200"
            : ""
        } hover:bg-gray-50 focus:bg-gray-50 outline-none`}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setShowEditModal(inquiry.id);
            setFormData({
              course: inquiry.course,
              fullName: inquiry.fullName,
              email: inquiry.email,
              countryCode: "+977", // Default, as we don't store countryCode separately
              mobileNumber: inquiry.mobileNumber,
              message: inquiry.message,
              status: inquiry.status,
            });
          }
        }}
      >
        <div className="py-2 px-4 w-[80px] text-right">{inquiry.id}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.course}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.fullName}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.email}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.mobileNumber}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.message}</div>
        <div className="py-2 px-4 w-[150px] text-center">
          {format(inquiry.createdAt, "yyyy-MM-dd HH:mm")}
        </div>
        <div className="py-2 px-4 w-[200px] flex justify-center space-x-2">
          <button
            onClick={() => {
              setShowEditModal(inquiry.id);
              setFormData({
                course: inquiry.course,
                fullName: inquiry.fullName,
                email: inquiry.email,
                countryCode: "+977",
                mobileNumber: inquiry.mobileNumber,
                message: inquiry.message,
                status: inquiry.status,
              });
            }}
            className="text-blue-500 hover:text-blue-700"
            data-tooltip-id="edit-tooltip"
            data-tooltip-content="Edit this inquiry"
            aria-label="Edit inquiry"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(inquiry.id)}
            className="text-red-500 hover:text-red-700"
            data-tooltip-id="delete-tooltip"
            data-tooltip-content="Delete this inquiry"
            aria-label="Delete inquiry"
          >
            Delete
          </button>
          <button
            onClick={() => handleMarkAsResolved(inquiry.id)}
            className="text-green-500 hover:text-green-700"
            data-tooltip-id="resolve-tooltip"
            data-tooltip-content="Mark as resolved"
            aria-label="Mark inquiry as resolved"
            disabled={inquiry.status === "Resolved"}
          >
            Resolve
          </button>
          <button
            onClick={() => handleSendEmail(inquiry)}
            className="text-purple-500 hover:text-purple-700"
            data-tooltip-id="email-tooltip"
            data-tooltip-content="Send email"
            aria-label="Send email to inquiry"
          >
            Email
          </button>
        </div>
      </div>
    );
  };

  const renderContactRow = ({ index, data }) => {
    const inquiry = data[index];
    return (
      <div
        className={`flex border-b ${
          messagedContacts.includes(inquiry.id)
            ? "bg-green-100"
            : inquiry.status === "Resolved"
            ? "bg-gray-200"
            : ""
        } hover:bg-gray-50 focus:bg-gray-50 outline-none`}
        tabIndex={0}
      >
        <div className="py-2 px-4 w-[80px] text-center">
          <input
            type="checkbox"
            checked={selectedContacts.includes(inquiry.id)}
            onChange={() => handleSelectContact(inquiry.id)}
            disabled={messagedContacts.includes(inquiry.id) || inquiry.status === "Resolved"}
            aria-label={`Select inquiry ${inquiry.id}`}
          />
        </div>
        <div className="py-2 px-4 w-[80px] text-right">{inquiry.id}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.course}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.fullName}</div>
        <div className="py-2 px-4 flex-1 text-left">{inquiry.mobileNumber}</div>
        <div className="py-2 px-4 flex-1 text-left">
          {inquiry.status === "Resolved" ? (
            <span className="text-gray-600 font-semibold">Resolved</span>
          ) : messagedContacts.includes(inquiry.id) || inquiry.status === "Messaged" ? (
            <span className="text-green-600 font-semibold">✓ Messaged</span>
          ) : (
            <span className="text-yellow-600 font-semibold">Pending</span>
          )}
        </div>
      </div>
    );
  };

  if (loading && inquiries.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
      </div>
    );
  }

  if (error && inquiries.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-blue-900 mb-6 text-center">
          Inquiry Management
        </h1>

        {/* Tabs */}
        <div className="flex justify-center mb-6 space-x-4">
          {["view", "contact", "create"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === tab
                  ? "bg-blue-500 text-white shadow-lg"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              aria-label={`Switch to ${tab} tab`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Filters */}
        {activeTab !== "create" && (
          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <input
                type="text"
                placeholder="Search by name, email, or course"
                onChange={(e) => debouncedSetSearchTerm(e.target.value)}
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Search inquiries"
              />
              <select
                value={courseFilter}
                onChange={(e) => setCourseFilter(e.target.value)}
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Filter by course"
              >
                <option value="">All Courses</option>
                {courses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Filter by status"
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Messaged">Messaged</option>
                <option value="Resolved">Resolved</option>
              </select>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, start: e.target.value }))
                }
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Start date filter"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, end: e.target.value }))
                }
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="End date filter"
              />
            </div>
            <div className="flex justify-end mt-4 space-x-2">
              <button
                onClick={exportToCSV}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-300"
                data-tooltip-id="export-tooltip"
                data-tooltip-content="Export inquiries to CSV"
                aria-label="Export inquiries to CSV"
              >
                Export CSV
              </button>
              {activeTab === "contact" && selectedContacts.length > 0 && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowConfirm("batchDelete")}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300"
                    data-tooltip-id="batch-delete-tooltip"
                    data-tooltip-content="Delete selected inquiries"
                    aria-label="Delete selected inquiries"
                  >
                    Delete Selected
                  </button>
                  <button
                    onClick={() => setShowConfirm("batchMessage")}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all duration-300"
                    data-tooltip-id="batch-message-tooltip"
                    data-tooltip-content="Send WhatsApp messages to selected inquiries"
                    aria-label="Send WhatsApp messages to selected inquiries"
                  >
                    Message Selected
                  </button>
                  <button
                    onClick={() => setShowConfirm("batchResolve")}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-300"
                    data-tooltip-id="batch-resolve-tooltip"
                    data-tooltip-content="Mark selected inquiries as resolved"
                    aria-label="Mark selected inquiries as resolved"
                  >
                    Resolve Selected
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Confirmation Modal for Batch Actions */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full transform transition-all duration-300 scale-100">
              <h2 className="text-xl font-bold mb-4">
                Confirm{" "}
                {showConfirm === "batchDelete"
                  ? "Deletion"
                  : showConfirm === "batchMessage"
                  ? "Messaging"
                  : "Resolution"}
              </h2>
              <p className="mb-6">
                Are you sure you want to{" "}
                {showConfirm === "batchDelete"
                  ? "delete"
                  : showConfirm === "batchMessage"
                  ? "message"
                  : "mark as resolved"}{" "}
                {selectedContacts.length} selected inquiries?
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowConfirm(null)}
                  className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400 transition-all duration-300"
                  aria-label="Cancel action"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    showConfirm === "batchDelete"
                      ? handleBatchDelete()
                      : showConfirm === "batchMessage"
                      ? handleSendMessages(selectedContacts)
                      : handleBatchMarkAsResolved()
                  }
                  className={`px-4 py-2 rounded text-white transition-all duration-300 ${
                    showConfirm === "batchDelete"
                      ? "bg-red-500 hover:bg-red-600"
                      : showConfirm === "batchMessage"
                      ? "bg-green-500 hover:bg-green-600"
                      : "bg-gray-500 hover:bg-gray-600"
                  }`}
                  disabled={isSending}
                  aria-label="Confirm action"
                >
                  {isSending ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Tab with Virtualized List */}
        {activeTab === "view" && (
          <div className="shadow rounded-lg bg-white">
            <div className="sticky top-0 bg-blue-500 text-white flex">
              <div
                className="py-2 px-4 w-[80px] cursor-pointer text-right"
                onClick={() => handleSort("id")}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("id")}
                aria-label="Sort by ID"
              >
                ID {sortConfig[0]?.key === "id" && (sortConfig[0].direction === "asc" ? "↑" : "↓")}
              </div>
              <div
                className="py-2 px-4 flex-1 cursor-pointer text-left"
                onClick={() => handleSort("course")}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("course")}
                aria-label="Sort by Course"
              >
                Course {sortConfig[0]?.key === "course" && (sortConfig[0].direction === "asc" ? "↑" : "↓")}
              </div>
              <div
                className="py-2 px-4 flex-1 cursor-pointer text-left"
                onClick={() => handleSort("fullName")}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("fullName")}
                aria-label="Sort by Full Name"
              >
                Full Name {sortConfig[0]?.key === "fullName" && (sortConfig[0].direction === "asc" ? "↑" : "↓")}
              </div>
              <div
                className="py-2 px-4 flex-1 cursor-pointer text-left"
                onClick={() => handleSort("email")}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("email")}
                aria-label="Sort by Email"
              >
                Email {sortConfig[0]?.key === "email" && (sortConfig[0].direction === "asc" ? "↑" : "↓")}
              </div>
              <div className="py-2 px-4 flex-1 text-left">Mobile</div>
              <div className="py-2 px-4 flex-1 text-left">Message</div>
              <div
                className="py-2 px-4 w-[150px] cursor-pointer text-center"
                onClick={() => handleSort("createdAt")}
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && handleSort("createdAt")}
                aria-label="Sort by Created At"
              >
                Created {sortConfig[0]?.key === "createdAt" && (sortConfig[0].direction === "asc" ? "↑" : "↓")}
              </div>
              <div className="py-2 px-4 w-[200px] text-center">Actions</div>
            </div>
            <Virtuoso
              style={{ height: listHeight }}
              data={currentInquiries}
              itemContent={(index, inquiry) => renderViewRow({ index, data: currentInquiries })}
              totalCount={currentInquiries.length}
            />
          </div>
        )}

        {/* Contact Tab with Virtualized List */}
        {activeTab === "contact" && (
          <div className="shadow rounded-lg bg-white">
            <div className="flex justify-between p-4 sticky top-0 bg-white z-10">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedContacts.length === currentInquiries.length}
                  onChange={handleSelectAll}
                  aria-label="Select all inquiries"
                />
                <span className="ml-2">Select All</span>
              </label>
              <button
                onClick={() => setShowConfirm("batchMessage")}
                className={`px-4 py-2 rounded text-white font-semibold transition-all duration-300 ${
                  selectedContacts.length > 0 ? "bg-green-500 hover:bg-green-600" : "bg-gray-400"
                }`}
                disabled={selectedContacts.length === 0}
                aria-label="Send WhatsApp messages to selected inquiries"
              >
                Send WhatsApp Messages
              </button>
            </div>
            <div className="sticky top-[72px] bg-blue-500 text-white flex">
              <div className="py-2 px-4 w-[80px] text-center">Select</div>
              <div className="py-2 px-4 w-[80px] text-right">ID</div>
              <div className="py-2 px-4 flex-1 text-left">Course</div>
              <div className="py-2 px-4 flex-1 text-left">Name</div>
              <div className="py-2 px-4 flex-1 text-left">Mobile</div>
              <div className="py-2 px-4 flex-1 text-left">Status</div>
            </div>
            <Virtuoso
              style={{ height: listHeight }}
              data={currentInquiries}
              itemContent={(index, inquiry) => renderContactRow({ index, data: currentInquiries })}
              totalCount={currentInquiries.length}
            />
          </div>
        )}

        {/* Create/Edit Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full transform transition-all duration-300 scale-100">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                {showCreateModal ? "Create Inquiry" : "Edit Inquiry"}
              </h2>
              <form onSubmit={showCreateModal ? handleCreate : handleUpdate} className="space-y-6">
                <SelectInput
                  label="Select a Course"
                  name="course"
                  value={formData.course}
                  onChange={handleCreateChange}
                  options={courses}
                  error={createErrors.course}
                  icon="📚"
                />
                <TextInput
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleCreateChange}
                  placeholder="Enter your full name"
                  error={createErrors.fullName}
                  icon="👤"
                />
                <TextInput
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleCreateChange}
                  placeholder="Enter your email address"
                  type="email"
                  error={createErrors.email}
                  icon="✉️"
                />
                <div className="flex space-x-4">
                  <SelectInput
                    label="Country Code"
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleCreateChange}
                    options={countryCodes.map((c) => `${c.code} (${c.name})`)}
                    error={createErrors.countryCode}
                    icon="🌍"
                    className="w-1/3"
                  />
                  <TextInput
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleCreateChange}
                    placeholder="Enter your mobile number"
                    type="tel"
                    error={createErrors.mobileNumber}
                    icon="📱"
                    className="w-2/3"
                  />
                </div>
                <TextAreaInput
                  label="Your Message"
                  name="message"
                  value={formData.message}
                  onChange={handleCreateChange}
                  placeholder="Share your goals or questions..."
                  error={createErrors.message}
                  icon="💬"
                />
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleCreateChange}
                    className="w-full p-3 border rounded-lg text-gray-600 focus:ring-2 focus:ring-blue-500 transition-all duration-200 border-gray-300 focus:outline-none"
                    aria-label="Inquiry status"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Messaged">Messaged</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(null);
                      setFormData({
                        course: "",
                        fullName: "",
                        email: "",
                        countryCode: "+977",
                        mobileNumber: "",
                        message: "",
                        status: "Pending",
                      });
                      setCreateErrors({});
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
                    aria-label="Cancel form"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 bg-blue-600 text-white rounded-lg font-medium transition-all duration-300 ${
                      loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-700"
                    }`}
                    disabled={loading}
                    aria-label={showCreateModal ? "Create inquiry" : "Update inquiry"}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                    ) : showCreateModal ? "Submit Inquiry" : "Update Inquiry"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create Tab */}
        {activeTab === "create" && (
          <div className="bg-gradient-to-b from-blue-950 to-gray-100 min-h-screen">
            {/* Header Section */}
            <div className="bg-blue-900 text-white py-12 px-4 md:px-8">
              <div className="max-w-7xl mx-auto text-center">
                <h1 className="text-4xl md:text-5xl font-extrabold mb-4 animate-slow-fade-in">
                  Create New Inquiry
                  <span className="block text-yellow-500 mt-2 animate-slow-fade-in delay-1">
                    Start Managing Your Leads!
                  </span>
                </h1>
                <p className="text-lg md:text-xl max-w-2xl mx-auto animate-slow-fade-in delay-2">
                  Add a new inquiry to track and manage potential students for your courses.
                </p>
              </div>
            </div>

            {/* Form Section */}
            <div className="max-w-7xl mx-auto px-6 py-10 md:py-16">
              <div className="bg-white p-6 rounded-2xl shadow-xl animate-slow-fade-in">
                <h2 className="text-2xl font-bold text-blue-900 mb-6">New Inquiry Form</h2>
                <form onSubmit={handleCreate} className="space-y-6">
                  <SelectInput
                    label="Select a Course"
                    name="course"
                    value={formData.course}
                    onChange={handleCreateChange}
                    options={courses}
                    error={createErrors.course}
                    icon="📚"
                  />
                  <TextInput
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleCreateChange}
                    placeholder="Enter full name"
                    error={createErrors.fullName}
                    icon="👤"
                  />
                  <TextInput
                    label="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleCreateChange}
                    placeholder="Enter email address"
                    type="email"
                    error={createErrors.email}
                    icon="✉️"
                  />
                  <div className="flex space-x-4">
                    <SelectInput
                      label="Country Code"
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleCreateChange}
                      options={countryCodes.map((c) => `${c.code} (${c.name})`)}
                      error={createErrors.countryCode}
                      icon="🌍"
                      className="w-1/3"
                    />
                    <TextInput
                      label="Mobile Number"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleCreateChange}
                      placeholder="Enter mobile number"
                      type="tel"
                      error={createErrors.mobileNumber}
                      icon="📱"
                      className="w-2/3"
                    />
                  </div>
                  <TextAreaInput
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleCreateChange}
                    placeholder="Enter inquiry details or questions..."
                    error={createErrors.message}
                    icon="💬"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg font-semibold transform transition-all duration-300 shadow-lg ${
                      loading ? "opacity-50 cursor-not-allowed" : "hover:from-blue-700 hover:to-blue-900 hover:scale-105"
                    }`}
                  >
                    {loading ? "Submitting..." : "Submit Inquiry"}
                  </button>
                </form>
              </div>
            </div>

            {/* Confirmation Modal for Create */}
            {showCreateConfirmation && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full transform transition-all scale-100 animate-slow-fade-in">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">Confirm New Inquiry</h3>
                  <div className="space-y-3 text-gray-700">
                    <p><strong>Course Wrote:</strong> {formData.course}</p>
                    <p><strong>Full Name:</strong> {formData.fullName}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                    <p><strong>Mobile Number:</strong> {formData.countryCode}{formData.mobileNumber}</p>
                    <p><strong>Message:</strong> {formData.message}</p>
                    <p><strong>Status:</strong> {formData.status}</p>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      onClick={() => setShowCreateConfirmation(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
                    >
                      Edit
                    </button>
                    <button
                      onClick={confirmCreateSubmission}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Inline CSS for Animations */}
            <style>
              {`
                @keyframes slow-fade-in {
                  0% { opacity: 0; transform: translateY(20px); }
                  100% { opacity: 1; transform: translateY(0); }
                }
                .animate-slow-fade-in {
                  animation: slow-fade-in 2s ease-out;
                }
                .delay-1 {
                  animation-delay: 0.5s;
                }
                .delay-2 {
                  animation-delay: 1s;
                }
              `}
            </style>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between mt-6 px-4">
            <button
              onClick={() => {
                setCurrentPage((prev) => Math.max(prev - 1, 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all duration-300"
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              Previous
            </button>
            <span aria-label={`Page ${currentPage} of ${totalPages}`}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => {
                setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all duration-300"
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        )}

        {/* Tooltips */}
        <Tooltip id="edit-tooltip" place="top" />
        <Tooltip id="delete-tooltip" place="top" />
        <Tooltip id="resolve-tooltip" place="top" />
        <Tooltip id="email-tooltip" place="top" />
        <Tooltip id="export-tooltip" place="top" />
        <Tooltip id="batch-delete-tooltip" place="top" />
        <Tooltip id="batch-message-tooltip" place="top" />
        <Tooltip id="batch-resolve-tooltip" place="top" />

        {/* Toast Notifications */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </div>
  );
};

export default Inquiry;
